import { type ReactNode } from 'react';
import * as Tooltip from '~/components/ui/Tooltip';
import { PricingCardMessages } from '~/components/ui/pricing/common';
import { PricingPerks } from '~/components/ui/pricing/common';
import type { PricingCardMessage, PricingPerk } from '~/lib/stores/pricing/types';

export interface PricingCardBaseProps {
  title: ReactNode;
  price: ReactNode;
  priceLabel?: ReactNode;
  billedLabel?: ReactNode;
  selector?: ReactNode;
  children: ReactNode;
  perks: {
    label: string;
    items: PricingPerk[];
  };
  messages: PricingCardMessage[];
  highlighted?: boolean;
}

export const PricingCardBase = ({
  title,
  price,
  priceLabel,
  billedLabel,
  selector,
  children,
  perks,
  messages,
  highlighted,
}: PricingCardBaseProps) => {
  const messagesUnderSelector = messages.filter((message) => message.placement === 'under-selector');
  const messagesUnderAction = messages.filter((message) => message.placement === 'under-action');

  return (
    <Tooltip.Provider>
      <div className="relative flex flex-col gap-6 border border-bolt-ds-borderOutline rounded-lg bg-bolt-ds-surfaceOne p-4.5">
        {highlighted && (
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-bolt-ds-brand/0 via-bolt-ds-brand to-bolt-ds-brand/0" />
        )}
        <div className="flex flex-col gap-3">
          <span className="text-base font-semibold">{title}</span>

          <div className="flex items-center gap-2">
            <span className="text-4xl font-medium text-bolt-ds-textPrimary">
              {typeof price === 'number' ? `$${price}` : price}
            </span>

            {priceLabel && (
              <div className="flex flex-col text-xs">
                <span>{priceLabel}</span>

                {billedLabel && <span>{billedLabel}</span>}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1.5 empty:hidden">
          {selector}

          {messagesUnderSelector.length > 0 && <PricingCardMessages messages={messagesUnderSelector} />}
        </div>

        <div className="flex flex-col gap-2">
          {children}

          {messagesUnderAction.length > 0 && <PricingCardMessages messages={messagesUnderAction} />}
        </div>

        <PricingPerks label={perks.label} items={perks.items} />
      </div>
    </Tooltip.Provider>
  );
};
